import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../css/PaymentsModal.css';
import { apiFetch } from '../utils/apiClient';
import { formatMoney } from '../i18n';
import { useBillingCatalog, getBillingCurrency, saveBillingCurrency } from '../utils/billingCatalog';

export default function PaymentsModal({ isOpen, onClose, tierType = 'individual' }) {
  const { t } = useTranslation();
  const [currency, setCurrency] = useState(getBillingCurrency);
  const initialPeriod = () => new URLSearchParams(window.location.search).get('interval') === 'yearly' ? 'yearly' : 'monthly';
  const [paymentPeriod, setPaymentPeriod] = useState(initialPeriod);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const { catalog, loading, failed } = useBillingCatalog(currency, isOpen);
  const price = catalog?.prices.find(item => item.plan === tierType && item.interval === paymentPeriod);
  const monthly = catalog?.prices.find(item => item.plan === tierType && item.interval === 'monthly');
  const annualSavings = monthly && price?.interval === 'yearly' ? monthly.unitAmount * 12 - price.unitAmount : 0;
  useEffect(() => { setError(''); }, [currency, tierType, isOpen]);
  useEffect(() => {
    if (catalog && !catalog.prices.some(item => item.plan === tierType && item.interval === paymentPeriod)) setPaymentPeriod('monthly');
  }, [catalog, tierType, paymentPeriod]);
  if (!isOpen) return null;

  const handleSubmit = async event => {
    event.preventDefault();
    if (!price?.available || !catalog?.enabled || isProcessing) return;
    setIsProcessing(true);
    setError('');
    try {
      const response = await apiFetch('/api/create-checkout-session', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: tierType, interval: paymentPeriod, currency }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const codes = ['CHECKOUT_UNAVAILABLE', 'INVALID_OFFER', 'ACCOUNT_MISMATCH', 'SUBSCRIPTION_EXISTS', 'BILLING_REVIEW_REQUIRED', 'CHECKOUT_IN_PROGRESS', 'CHECKOUT_EXPIRED'];
        throw new Error(codes.includes(data.code) ? `billing.errors.${data.code}` : 'billing.checkoutError');
      }
      const { url } = await response.json();
      if (!url) throw new Error();
      window.location.assign(url);
    } catch (error) { setError(t(error.message.startsWith('billing.') ? error.message : 'billing.checkoutError')); }
    finally { setIsProcessing(false); }
  };

  return <div className="payments-modal-overlay">
    <div className="payments-modal-content" role="dialog" aria-modal="true" aria-labelledby="checkout-title">
      <h2 id="checkout-title">{t('billing.subscribe')} — {t(`billing.${tierType}`)}</h2>
      <form onSubmit={handleSubmit} className="payment-form">
        <label>{t('billing.currency')}
          <select value={currency} disabled={isProcessing} onChange={event => {
            setCurrency(event.target.value); saveBillingCurrency(event.target.value);
          }}><option value="gbp">GBP (£)</option><option value="eur">EUR (€)</option></select>
        </label>
        {loading && <p>{t('common.loading')}</p>}
        {failed && <p role="alert">{t('billing.catalogError')}</p>}
        {!loading && !failed && <>
          <div className="payment-period-selection">
            {['monthly', 'yearly'].filter(interval => catalog?.prices.some(item => item.plan === tierType && item.interval === interval)).map(interval =>
              <label key={interval} className="period-label"><input type="radio" name="paymentPeriod" value={interval}
                checked={paymentPeriod === interval} disabled={isProcessing} onChange={() => setPaymentPeriod(interval)} />
                {t(`billing.${interval}`)}
              </label>)}
          </div>
          {price ? <div className="payments-summary">
            <p className="price-display"><span>{formatMoney(price.unitAmount, price.currency)}</span> {t(paymentPeriod === 'monthly' ? 'billing.perMonth' : 'billing.perYear')}</p>
            {price.taxBehavior !== 'unspecified' && <p>{t(price.taxBehavior === 'inclusive' ? 'billing.inclusive' : 'billing.exclusive')}</p>}
            {annualSavings > 0 && <p>{t('billing.savings', { amount: formatMoney(annualSavings, currency) })}</p>}
          </div> : <p>{t('billing.unavailable')}</p>}
          {currency === 'eur' && !price?.available && <p role="status">{t('billing.launchPending')}</p>}
        </>}
        <p>{t('billing.account')}</p>
        <p>{t('billing.savedCurrency')}</p>
        {error && <p role="alert">{error}</p>}
        <div className="payment-actions">
          <button type="submit" className="payment-button" disabled={loading || failed || isProcessing || !catalog?.enabled || !price?.available}>
            {t(isProcessing ? 'common.processing' : 'billing.checkout')}
          </button>
          <button type="button" className="cancel-button" onClick={onClose} disabled={isProcessing}>{t('common.close')}</button>
        </div>
      </form>
    </div>
  </div>;
}
